import React, { useState, useRef, useEffect } from 'react';
import './RemoteDrawingPad.css';

const RemoteDrawingPad = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [wsConnection, setWsConnection] = useState(null);
  const [connected, setConnected] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [deviceId] = useState(`device_${Math.random().toString(36).substring(2, 10)}`);

  // Drawing tool states
  const [selectedTool, setSelectedTool] = useState('pen');
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(5);
  
  // Get session ID from URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const id = queryParams.get('session');
    if (id) {
      setSessionId(id);
      initializeWebSocket(id);
    } else {
      setError('No session ID provided. Please scan the QR code from the main whiteboard.');
    }
    
    // Cleanup function for WebSocket
    return () => {
      if (wsConnection) {
        console.log('Cleaning up WebSocket connection on unmount');
        wsConnection.close();
      }
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  // ⚠️ We can't include initializeWebSocket in deps as it would cause infinite reconnections

  // Initialize WebSocket connection
  const initializeWebSocket = (roomId) => {
    // Close any existing connection
    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
      console.log('Closing existing WebSocket connection');
      wsConnection.close();
    }
    
    // Use api.andrewslearning.com domain for WebSocket connection
    // URL format should match: /wschat/whiteboard/device/{room_id}/{device_id}/{user_id}/
    const wsURL = `wss://api.andrewslearning.com/wschat/whiteboard/device/${roomId}/${deviceId}/${sessionId || 'anonymous'}/`;
    
    console.log('Connecting to WebSocket URL:', wsURL);
    
    // Create new WebSocket connection
    const ws = new WebSocket(wsURL);
    
    // Add connection timeout
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.log('WebSocket connection timed out');
        setError('Connection timed out. Please try refreshing the page.');
        setConnected(false);
        ws.close();
      }
    }, 10000);
    
    ws.onopen = () => {
      console.log('WebSocket connection opened successfully');
      clearTimeout(connectionTimeout);
      setConnected(true);
      setConnectionStatus('Connected');
      
      // Send device info
      ws.send(JSON.stringify({
        type: 'device_info',
        deviceId: deviceId,
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight
      }));
      
      // Set up ping interval to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          console.log('Sending ping to keep connection alive');
          ws.send(JSON.stringify({ type: 'ping' }));
        } else {
          clearInterval(pingInterval);
        }
      }, 30000); // Send ping every 30 seconds
      
      // Store the interval to clear it later
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data);
        
        if (data.type === 'pong') {
          console.log('Received pong from server');
          return;
        }
        
        if (data.type === 'connection_established') {
          console.log('Device connection established', data);
          setConnected(true);
        }
        else if (data.type === 'device_paired') {
          console.log(`Device paired with user: ${data.username}`);
          setError('');
          // You could update UI to show the user this device is paired with
        }
        else if (data.type === 'tool_change') {
          // Update tool settings based on the main whiteboard
          if (data.tool) setSelectedTool(data.tool);
          if (data.color) setSelectedColor(data.color);
          if (data.lineWidth) setLineWidth(data.lineWidth);
        }
        else if (data.type === 'error') {
          setError(data.message || 'An error occurred');
        }
      } catch (error) {
        console.error('Error processing message:', error);
      }
    };

    ws.onclose = (event) => {
      console.log(`WebSocket closed with code: ${event.code}, reason: ${event.reason}`);
      clearTimeout(connectionTimeout);
      
      if (ws.pingInterval) {
        clearInterval(ws.pingInterval);
      }
      
      setConnected(false);
      setConnectionStatus('Disconnected');
      
      if (event.code !== 1000) {
        // Unexpected close, try to reconnect after a delay
        setError('Connection lost. Attempting to reconnect...');
        setTimeout(() => {
          if (roomId) {
            console.log('Attempting to reconnect...');
            initializeWebSocket(roomId);
          }
        }, 5000);
      } else {
        setError('Connection closed. Please reconnect.');
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setError('Connection error. Please try again.');
      setConnected(false);
      setConnectionStatus('Error');
      
      // Clear the connection timeout to prevent double handling
      clearTimeout(connectionTimeout);
    };

    setWsConnection(ws);
  };

  // Drawing functions
  const startDrawing = (e) => {
    if (!connected || !wsConnection) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    
    let x, y, pressure = 0.5;
    
    // Handle different event types
    if (e.touches && e.touches[0]) {
      // Touch event
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else if (e.pointerType) {
      // Pointer event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
      pressure = e.pressure > 0 ? e.pressure : 0.5;
    } else {
      // Mouse event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    // Send draw start event to main whiteboard
    wsConnection.send(JSON.stringify({
      type: 'draw_action',
      action: 'start',
      x: x / canvas.width,
      y: y / canvas.height,
      pressure,
      tool: selectedTool,
      color: selectedColor,
      lineWidth,
      isEraser: selectedTool === 'eraser'
    }));
    
    // Draw locally on the remote pad
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = selectedTool === 'eraser' ? '#ffffff' : selectedColor;
    ctx.lineWidth = lineWidth * pressure;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const draw = (e) => {
    if (!isDrawing || !connected || !wsConnection) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    
    let x, y, pressure = 0.5;
    
    // Handle different event types
    if (e.touches && e.touches[0]) {
      // Touch event
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else if (e.pointerType) {
      // Pointer event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
      pressure = e.pressure > 0 ? e.pressure : 0.5;
    } else {
      // Mouse event
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    // Send draw move event to main whiteboard
    wsConnection.send(JSON.stringify({
      type: 'draw_action',
      action: 'move',
      x: x / canvas.width,
      y: y / canvas.height,
      pressure,
      tool: selectedTool
    }));
    
    // Draw locally on the remote pad
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const finishDrawing = () => {
    if (!isDrawing || !connected || !wsConnection) return;
    
    setIsDrawing(false);
    
    // Send draw end event to main whiteboard
    wsConnection.send(JSON.stringify({
      type: 'draw_action',
      action: 'end'
    }));
    
    // Finish the path on local canvas
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.closePath();
  };

  // Update tool settings
  const changeTool = (tool) => {
    setSelectedTool(tool);
    if (connected && wsConnection) {
      wsConnection.send(JSON.stringify({
        type: 'tool_change',
        tool
      }));
    }
  };

  const changeColor = (color) => {
    setSelectedColor(color);
    if (connected && wsConnection) {
      wsConnection.send(JSON.stringify({
        type: 'tool_change',
        color
      }));
    }
  };

  const changeLineWidth = (width) => {
    setLineWidth(width);
    if (connected && wsConnection) {
      wsConnection.send(JSON.stringify({
        type: 'tool_change',
        lineWidth: width
      }));
    }
  };

  // Handle canvas resize
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const parent = canvas.parentElement;
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight - 100; // Leave space for controls
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Clear the local canvas
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className="remote-pad-container">
      <div className="remote-pad-header">
        <h1>Remote Drawing Pad</h1>
        <div className="connection-status">
          <div className={`status-indicator ${connected ? 'connected' : 'disconnected'}`}></div>
          <span>{connected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="drawing-pad">
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={finishDrawing}
          onMouseOut={finishDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={finishDrawing}
          onTouchCancel={finishDrawing}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={finishDrawing}
          onPointerOut={finishDrawing}
          onPointerCancel={finishDrawing}
          touch-action="none"
        ></canvas>
      </div>
      
      <div className="remote-tools">
        <div className="tool-selector">
          <button 
            className={`tool-btn ${selectedTool === 'pen' ? 'selected' : ''}`} 
            onClick={() => changeTool('pen')}
          >
            Pen
          </button>
          <button 
            className={`tool-btn ${selectedTool === 'eraser' ? 'selected' : ''}`} 
            onClick={() => changeTool('eraser')}
          >
            Eraser
          </button>
        </div>
        
        <div className="color-picker">
          <input 
            type="color" 
            value={selectedColor} 
            onChange={(e) => changeColor(e.target.value)} 
          />
        </div>
        
        <div className="line-width">
          <input 
            type="range" 
            min="1" 
            max="20" 
            value={lineWidth} 
            onChange={(e) => changeLineWidth(parseInt(e.target.value))} 
          />
        </div>
        
        <button className="clear-btn" onClick={clearCanvas}>Clear Local</button>
      </div>
    </div>
  );
};

export default RemoteDrawingPad; 